import rtlPlugin from "stylis-plugin-rtl";
import { useState, useEffect } from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import authStore from "@/stores/authStore";
import AccountingFilter from "@/components/accounting/accountingFilter";
import AccountingTable from "@/components/accounting/accountingDocsTable";
import popupService from "@/services/popupService";
import api from "@/services/api";
import "./accountingStyles.css";
import { AccountingFilterRequest, AccountingDocSearchItem, AccountingFilterResponse } from "@/models/accounting";
import { checkPermissionsWithAlert, formatDate } from "@/util";
import { PermissionName } from "@/models/apiModels";
import FilterCancelIcon from "../styled/filter.cancel.icon";
import { useTranslation } from "react-i18next";
import filtersStore, { AccountingFilterData } from "@/stores/filtersStore";
import TotalCountComponent from "@/components/common/resultCountComponent";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const AccountingDocs = () => {
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);
    const [accountingDocs, setAccountingDocs] = useState<AccountingDocSearchItem[]>([]);
    const [accountingFilter, setAccountingFilter] = useState<AccountingFilterRequest>({
        pageIndex: 0,
        orderBy: "",
        orderAsc: false,
        keywords: "",
        providerName: "",
        fromDate: null,
        toDate: null,
        docType: null,
    });
    const [showAccountingTable, setShowAccountingTable] = useState(true);
    const [searchCounter, setSearchCounter] = useState<number>(0);
    const [collapseFilterCounter, setCollapseFilterCounter] = useState<number>(0);
    const [filterTabsCounter, setFilterTabsCounter] = useState<number>(0);
    const { t } = useTranslation();
    const [filterTabsArray, setFilterTabsArray] = useState<Array<{ id: string; name: string; value: string | null }>>(
        []
    );

    const filterTabs = (
        <div
            id="filter-tabs"
            className="accounting-filter-tab-container"
            style={filterTabsArray.length > 0 ? { marginTop: "10px" } : { marginTop: "0px" }}
        >
            {filterTabsArray.map((filterTab) => (
                <div key={filterTab.id} className="filter-tab-element font-style">
                    <span>
                        <span className="tab-color">{filterTab.name}</span> {filterTab.value}
                    </span>
                    <button className="filter-tab-button" onClick={() => removeFilterTab(filterTab.name, filterTab.id)}>
                        <FilterCancelIcon />
                    </button>
                </div>
            ))}
        </div>
    );

    const setFilterTabs = async () => {
        const filters = [
            {
                id: "keyword-input",
                name: accountingFilter.keywords,
                value: `${t("employee.QuickSearch")}:`,
            },
            {
                id: "from-date-input",
                name: accountingFilter.fromDate != null ? formatDate(accountingFilter.fromDate.toString()) : null,
                value: `${t("accounting.fromDate")}:`,
            },
            {
                id: "to-date-input",
                name: accountingFilter.toDate != null ? formatDate(accountingFilter.toDate.toString()) : null,
                value: `${t("accounting.endDate")}:`,
            },
            {
                id: "provider-name-input",
                name: accountingFilter.providerName,
                value: `${t("accounting.providerName")}:`,
            },
            {
                id: "doc-type-input",
                name: accountingFilter.docType?.toString() ?? "",
                value: `${t("accounting.docType")}:`,
            },
        ];

        const filteredTabs = filters
            .filter((filter) => filter.name !== null && filter.name !== "")
            .map((filter) => ({ id: filter.id, name: filter.value, value: filter.name }));

        setFilterTabsArray(filteredTabs);
    };

    const removeFilterTab = (tabName: string | undefined, tabId: string) => {
        const newArray = [...filterTabsArray];
        const index = newArray.findIndex((tab) => tab.name === tabName);
        newArray.splice(index, 1);
        setFilterTabsArray(newArray);

        switch (tabId) {
            case "keyword-input":
                setAccountingFilter(() => ({
                    ...accountingFilter,
                    keywords: "",
                }));
                break;
            case "provider-name-input":
                setAccountingFilter(() => ({
                    ...accountingFilter,
                    providerName: "",
                }));
                break;
            case "from-date-input":
                setAccountingFilter(() => ({
                    ...accountingFilter,
                    fromDate: null,
                }));
                break;
            case "to-date-input":
                setAccountingFilter(() => ({
                    ...accountingFilter,
                    toDate: null,
                }));
                break;
            case "doc-type-input":
                setAccountingFilter(() => ({
                    ...accountingFilter,
                    docType: null,
                }));
                break;
            default:
                break;
        }
    };

    const onSearchClick = async () => {
        setAccountingDocs([]);
        filtersStore.setAccountingFilterData(null);
        await setShowAccountingTable(false);
        setPageIndex(0);
        setShowAccountingTable(true);
        setSearchCounter(searchCounter + 1);
    };

    const setPageIndex = (index?: number) => {
        setAccountingFilter((prevData) => ({
            ...prevData,
            pageIndex: index ?? accountingFilter.pageIndex + 1,
        }));
    };

    async function getAccountingDocuments(): Promise<AccountingFilterResponse> {
        const filterStore = filtersStore.accountingFilterData;
        if (filterStore != null) {
            if (filterStore.filter?.pageIndex !== 0) {
                if (filterStore.filter) filterStore.filter.pageIndex = 0;
            }
            setAccountingFilter(filterStore.filter ?? new AccountingFilterRequest());
            setFilterTabsArray(filterStore.filterTabs);
            const responsePromise = api.getAccountingDocuments(filterStore.filter ?? new AccountingFilterRequest());
            return (await responsePromise).data;
        }

        const accountingFilterData = new AccountingFilterData();
        accountingFilterData.filter = accountingFilter;
        accountingFilterData.filterTabs = filterTabsArray;
        filtersStore.setAccountingFilterData(accountingFilterData);

        const responsePromise = api.getAccountingDocuments(accountingFilter);
        return (await responsePromise).data;
    }

    let startFetchingBusy = false;
    const startFetching = async () => {
        if (!checkPermissionsWithAlert(PermissionName.ListOfDocuments)) {
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const response = await getAccountingDocuments();
            if (accountingFilter.pageIndex === 0) {
                setTotal(response.total);
                setTotalFilter(response.totalFilter);
                setAccountingDocs(response.accountingDocuments);
            } else {
                setAccountingDocs((oldArray) => [...oldArray, ...response.accountingDocuments]);
            }
            setFilterTabsCounter(filterTabsCounter + 1);
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountingFilter.pageIndex, accountingFilter.orderBy, accountingFilter.orderAsc, searchCounter]);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            const fetchFilterTabs = async () => {
                await setFilterTabs();
            };
            fetchFilterTabs();
        }
        return () => {
            ignore = true;
        };
    }, [filterTabsCounter]);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            {checkPermissionsWithAlert(PermissionName.ListOfDocuments) ? (
                <div className="accounting-container">
                    <AccountingFilter
                        searchClick={onSearchClick}
                        total={total}
                        filter={accountingFilter}
                        setFilter={setAccountingFilter}
                        filterTabs={filterTabs}
                        collapseFilterClick={() => setCollapseFilterCounter(collapseFilterCounter + 1)}
                    />
                    <TotalCountComponent
                        title={t("employees.QuantityOfDocuments") as string}
                        allCount={total}
                        currentCount={totalFilter}
                    />
                    {showAccountingTable ? (
                        <AccountingTable
                            accountingDocs={accountingDocs}
                            filter={accountingFilter}
                            setFilter={setAccountingFilter}
                            total={total}
                            fetchMore={setPageIndex}
                        />
                    ) : (
                        ""
                    )}
                </div>
            ) : (
                ""
            )}
        </CacheProvider>
    );
};

export default AccountingDocs;
